import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { updateBackofficeOrganizationFeatures } from '../api';
import type {
  BackofficeOrganizationDetail,
  BackofficeOrganizationFeatures,
  UpdateBackofficeOrganizationFeaturesResponse,
} from '../types';
import { OrganizationFeatureFields, featuresEqual } from './OrganizationFeatureFields';
import { formatDateTime } from '../format';

export interface EditOrganizationFeaturesModalProps {
  organization: BackofficeOrganizationDetail;
  onClose: () => void;
  onUpdated: (response: UpdateBackofficeOrganizationFeaturesResponse) => void;
}

export function EditOrganizationFeaturesModal({
  organization,
  onClose,
  onUpdated,
}: EditOrganizationFeaturesModalProps) {
  const [features, setFeatures] = useState<BackofficeOrganizationFeatures>(organization.features);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const dirty = useMemo(
    () => !featuresEqual(features, organization.features),
    [features, organization.features]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dirty || saving) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await updateBackofficeOrganizationFeatures(organization.id, { features });
      onUpdated(response);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to update features');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-organization-features-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <div>
            <h2 id="edit-organization-features-title" className="text-title font-semibold">
              Edit organization&apos;s features
            </h2>
            <p className="mt-1 text-small text-neutral-500">
              {organization.name} - {organization.pricingTierName}
              {organization.tierSettings.validFrom
                ? ` since ${formatDateTime(organization.tierSettings.validFrom)}`
                : ''}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="focus-ring inline-flex size-9 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
            onClick={onClose}
            disabled={saving}
            aria-label="Close feature editor"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {error ? (
            <div className="mx-5 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
              {error}
            </div>
          ) : null}

          <OrganizationFeatureFields
            features={features}
            disabled={saving}
            onChange={(value) => {
              setFeatures(value);
              setError(null);
            }}
          />
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button
            type="submit"
            className="focus-ring inline-flex items-center rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!dirty || saving}
          >
            {saving ? 'Saving' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
